if (!customElements.get('pb-context-navigation')) {
  class PbContextNavigation extends HTMLElement {
    connectedCallback() {
      this.mobileToggle = this.querySelector('.pb-context-nav__mobile-toggle');
      this.links = Array.from(this.querySelectorAll('a.pb-context-nav__link[href]'));
      this.handleMobileToggle = this.handleMobileToggle.bind(this);
      this.handleLinkClick = this.handleLinkClick.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.scheduleScrollspyUpdate = this.scheduleScrollspyUpdate.bind(this);
      this.scheduleStickyUpdate = this.scheduleStickyUpdate.bind(this);

      this.mobileToggle?.addEventListener('click', this.handleMobileToggle);
      this.links.forEach((link) => link.addEventListener('click', this.handleLinkClick));

      this.mediaQuery = window.matchMedia('(min-width: 768px)');
      this.mediaQuery.addEventListener('change', this.handleViewportChange);

      this.syncCurrentLinks();
      this.syncGroups();
      this.setupScrollspy();
      this.syncStickyWrapper();
      this.syncStickyContainer();
      this.syncHeaderTakeover();
    }

    disconnectedCallback() {
      this.cancelAnchorScroll();
      this.mobileToggle?.removeEventListener('click', this.handleMobileToggle);
      this.links?.forEach((link) => link.removeEventListener('click', this.handleLinkClick));
      this.mediaQuery?.removeEventListener('change', this.handleViewportChange);
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.scheduleScrollspyUpdate);
      window.removeEventListener('resize', this.scheduleScrollspyUpdate);
      if (this.scrollspyFrame) cancelAnimationFrame(this.scrollspyFrame);
      this.releaseStickyWrapper();
      this.releaseStickyContainer();
      this.releaseHeaderTakeover();
    }

    handleMobileToggle() {
      const isOpen = this.classList.toggle('is-mobile-open');
      this.mobileToggle.setAttribute('aria-expanded', String(isOpen));
    }

    handleViewportChange() {
      this.classList.remove('is-mobile-open');
      this.mobileToggle?.setAttribute('aria-expanded', 'false');
      this.syncStickyWrapper();
      this.syncStickyContainer();
      this.syncHeaderTakeover();
    }

    handleLinkClick(event) {
      const link = event.currentTarget;
      const target = this.getAnchorTarget(link);
      this.classList.remove('is-mobile-open');
      this.mobileToggle?.setAttribute('aria-expanded', 'false');

      if (target && this.dataset.smoothScroll === 'true') {
        event.preventDefault();
        this.cancelAnchorScroll();
        this.anchorScrollController = new AbortController();
        const signal = this.anchorScrollController.signal;
        const finish = () => {
          this.cancelAnchorScroll();
          if (!target.isConnected) return;
          const correction = target.getBoundingClientRect().top - this.getScrollOffset();
          if (Math.abs(correction) > 1) {
            window.scrollBy({ top: correction, behavior: 'instant' });
          }
          this.setActiveLink(link, 'location');
        };
        const cancel = () => this.cancelAnchorScroll();
        window.addEventListener('scrollend', finish, { once: true, signal });
        for (const type of ['wheel', 'touchstart', 'keydown']) {
          window.addEventListener(type, cancel, { once: true, passive: true, signal });
        }
        this.anchorScrollTimer = window.setTimeout(finish, 1000);
        const offset = this.getScrollOffset();
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
        window.scrollTo({ top, behavior });
        history.replaceState(null, '', `#${target.id}`);
        this.setActiveLink(link, 'location');
      }

    }

    cancelAnchorScroll() {
      this.anchorScrollController?.abort();
      this.anchorScrollController = null;
      window.clearTimeout(this.anchorScrollTimer);
      this.anchorScrollTimer = null;
    }

    syncCurrentLinks() {
      const currentPath = this.normalisePath(window.location.pathname);
      const currentHash = window.location.hash;

      this.links.forEach((link) => {
        let url;
        try {
          url = new URL(link.href, window.location.href);
        } catch (error) {
          return;
        }

        const samePath = this.normalisePath(url.pathname) === currentPath;
        const isAnchor = Boolean(url.hash);
        const active = isAnchor
          ? samePath && currentHash !== '' && url.hash === currentHash
          : samePath && url.origin === window.location.origin;

        if (active) this.setActiveLink(link, isAnchor ? 'location' : 'page');
      });
    }

    syncGroups() {
      const behavior = this.dataset.groupBehavior;
      this.querySelectorAll('.pb-context-nav__group').forEach((group) => {
        const hasActive = Boolean(group.querySelector('.is-active, [aria-current]'));
        group.closest('.pb-context-nav__entry')?.classList.toggle('is-active', hasActive);

        if (behavior === 'all') group.open = true;
        if (behavior === 'collapsed') group.open = false;
        if (behavior === 'active' && hasActive) group.open = true;
      });
    }

    setupScrollspy() {
      if (this.dataset.scrollspy !== 'true') return;

      this.anchorEntries = this.links
        .map((link) => ({ link, target: this.getAnchorTarget(link) }))
        .filter((entry) => entry.target);

      if (!this.anchorEntries.length) return;

      window.addEventListener('scroll', this.scheduleScrollspyUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleScrollspyUpdate);
      this.scheduleScrollspyUpdate();
    }

    scheduleScrollspyUpdate() {
      if (this.scrollspyFrame) return;

      this.scrollspyFrame = requestAnimationFrame(() => {
        this.scrollspyFrame = null;
        this.updateScrollspy();
      });
    }

    updateScrollspy() {
      if (!this.anchorEntries?.length) return;

      const activationLine = this.getScrollOffset() + 1;
      const positionedEntries = this.anchorEntries
        .map((entry) => ({
          ...entry,
          top: entry.target.getBoundingClientRect().top
        }))
        .sort((a, b) => a.top - b.top);
      const active = positionedEntries.reduce(
        (current, entry) => entry.top <= activationLine ? entry : current,
        null
      );

      if (!active || active.link.classList.contains('is-active')) return;
      this.setActiveLink(active.link, 'location', true);
      this.syncGroups();
    }

    getAnchorTarget(link) {
      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (error) {
        return null;
      }

      if (!url.hash || this.normalisePath(url.pathname) !== this.normalisePath(window.location.pathname)) {
        return null;
      }

      const id = decodeURIComponent(url.hash.slice(1));
      return id ? document.getElementById(id) : null;
    }

    getScrollOffset() {
      const ownOffset = parseFloat(getComputedStyle(this).getPropertyValue('--pb-context-sticky-offset')) || 0;
      const headerHeight = this.dataset.headerBehavior === 'replace'
        ? 0
        : this.trackedHeader
          ? Math.max(0, this.trackedHeader.getBoundingClientRect().bottom)
          : parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const navHeight = this.matchesStickyViewport() && this.dataset.orientation === 'horizontal'
        ? this.getBoundingClientRect().height
        : 0;
      const clearance = this.trackedHeader ? 0 : 12;
      return ownOffset + headerHeight + navHeight + clearance;
    }

    setActiveLink(link, currentType, anchorsOnly = false) {
      this.links.forEach((candidate) => {
        if (anchorsOnly && !this.getAnchorTarget(candidate)) return;
        candidate.classList.remove('is-active');
        candidate.removeAttribute('aria-current');
        candidate.closest('.pb-context-nav__entry')?.classList.remove('is-active');
      });

      link.classList.add('is-active');
      link.setAttribute('aria-current', currentType);
      link.closest('.pb-context-nav__entry')?.classList.add('is-active');
    }

    normalisePath(path) {
      const cleaned = path.replace(/\/+$/, '');
      return cleaned || '/';
    }

    matchesStickyViewport() {
      return this.dataset.sticky === 'all'
        || (this.dataset.sticky === 'desktop' && this.mediaQuery.matches);
    }

    syncStickyContainer() {
      this.releaseStickyContainer();
      if (this.dataset.orientation !== 'horizontal' || !this.matchesStickyViewport()) return;

      this.stickySection = this.closest('.shopify-section');
      if (!this.stickySection) return;

      const ownOffset = parseFloat(getComputedStyle(this).getPropertyValue('--pb-context-sticky-offset')) || 0;
      this.stickySection.style.setProperty('--pb-context-nav-section-offset', `${ownOffset}px`);
      this.stickySection.classList.add('pb-context-nav-sticky-section');
      this.stickySection.classList.toggle(
        'pb-context-nav-sticky-section--replace',
        this.dataset.headerBehavior === 'replace'
      );
      this.stickySection.style.setProperty(
        '--pb-context-nav-section-bg',
        getComputedStyle(this).getPropertyValue('--pb-context-bg').trim() || '#ffffff'
      );
      if (this.dataset.headerBehavior !== 'replace') this.startHeaderTracking();
    }

    startHeaderTracking() {
      PbContextNavigation.trackingOwners.add(this);
      this.trackedHeader = document.querySelector('store-header');
      window.addEventListener('scroll', this.scheduleStickyUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleStickyUpdate);
      if (this.trackedHeader) {
        this.headerResizeObserver = new ResizeObserver(this.scheduleStickyUpdate);
        this.headerResizeObserver.observe(this.trackedHeader);
      }
      this.updateStickyHeader();
    }

    scheduleStickyUpdate() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        this.updateStickyHeader();
      });
    }

    updateStickyHeader() {
      if (!this.stickySection) return;
      const bottom = this.trackedHeader
        ? Math.max(0, this.trackedHeader.getBoundingClientRect().bottom)
        : 0;
      const value = `${bottom.toFixed(2)}px`;
      if (value !== this.lastHeaderBottom) {
        this.stickySection.style.setProperty('--pb-context-nav-header-bottom', value);
        document.documentElement.style.setProperty('--pb-context-header-bottom', value);
        this.lastHeaderBottom = value;
      }
      const ownOffset = parseFloat(getComputedStyle(this).getPropertyValue('--pb-context-sticky-offset')) || 0;
      const rect = this.stickySection.getBoundingClientRect();
      const attached = rect.top <= bottom + ownOffset + 1 && rect.bottom > bottom;
      this.stickySection.classList.toggle('pb-context-nav-is-attached', attached);
      if (!this.trackedHeader) return;
      if (attached) PbContextNavigation.shadowOwners.add(this);
      else PbContextNavigation.shadowOwners.delete(this);
      this.trackedHeader.classList.toggle('pb-context-nav-header-attached',
        [...PbContextNavigation.shadowOwners].some((owner) => owner.trackedHeader === this.trackedHeader));
    }

    syncStickyWrapper() {
      this.releaseStickyWrapper();
      if (
        this.dataset.orientation !== 'vertical'
        || !this.classList.contains('pb-context-nav--sticky-start')
        || !this.matchesStickyViewport()
      ) return;

      const wrapper = this.closest('.shopify-block');
      if (!wrapper || !wrapper.parentElement?.classList.contains('pb-col')) return;

      const ownOffset = parseFloat(getComputedStyle(this).getPropertyValue('--pb-context-sticky-offset')) || 0;
      wrapper.style.setProperty('--pb-context-sticky-offset', `${ownOffset}px`);
      wrapper.classList.add('pb-context-nav-sticky-wrapper');
      wrapper.classList.toggle(
        'pb-context-nav-sticky-wrapper--replace',
        this.dataset.headerBehavior === 'replace'
      );
      this.stickyWrapper = wrapper;
    }

    releaseStickyWrapper() {
      if (!this.stickyWrapper) return;
      this.stickyWrapper.classList.remove(
        'pb-context-nav-sticky-wrapper',
        'pb-context-nav-sticky-wrapper--replace'
      );
      this.stickyWrapper.style.removeProperty('--pb-context-sticky-offset');
      this.stickyWrapper = null;
    }

    releaseStickyContainer() {
      window.removeEventListener('scroll', this.scheduleStickyUpdate);
      window.removeEventListener('resize', this.scheduleStickyUpdate);
      this.headerResizeObserver?.disconnect();
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = null;
      this.lastHeaderBottom = null;
      PbContextNavigation.shadowOwners.delete(this);
      PbContextNavigation.trackingOwners.delete(this);
      if (this.trackedHeader) {
        this.trackedHeader.classList.toggle('pb-context-nav-header-attached',
          [...PbContextNavigation.shadowOwners].some((owner) => owner.trackedHeader === this.trackedHeader));
      }
      this.trackedHeader = null;
      if (!PbContextNavigation.trackingOwners.size) document.documentElement.style.removeProperty('--pb-context-header-bottom');
      if (!this.stickySection) return;
      this.stickySection.classList.remove(
        'pb-context-nav-sticky-section',
        'pb-context-nav-sticky-section--replace',
        'pb-context-nav-is-attached'
      );
      this.stickySection.style.removeProperty('--pb-context-nav-section-offset');
      this.stickySection.style.removeProperty('--pb-context-nav-header-bottom');
      this.stickySection.style.removeProperty('--pb-context-nav-section-bg');
      this.stickySection = null;
    }

    syncHeaderTakeover() {
      const shouldTakeOver = this.dataset.orientation === 'horizontal'
        && this.dataset.headerBehavior === 'replace'
        && this.matchesStickyViewport();

      if (shouldTakeOver) {
        this.requestHeaderTakeover();
      } else {
        this.releaseHeaderTakeover();
      }
    }

    requestHeaderTakeover() {
      if (this.hasHeaderTakeover) return;
      this.hasHeaderTakeover = true;
      PbContextNavigation.headerOwners.add(this);
      document.documentElement.classList.add('pb-context-nav-replaces-header');

      const headerSection = document.querySelector('.cc-header');
      const storeHeader = document.querySelector('store-header');
      if (headerSection?.classList.contains('cc-header--sticky')) {
        headerSection.dataset.pbContextWasSticky = 'true';
        headerSection.classList.remove('cc-header--sticky');
      }
      storeHeader?.classList.remove('is-out');
    }

    releaseHeaderTakeover() {
      if (!this.hasHeaderTakeover) return;
      this.hasHeaderTakeover = false;
      PbContextNavigation.headerOwners.delete(this);
      if (PbContextNavigation.headerOwners.size) return;

      document.documentElement.classList.remove('pb-context-nav-replaces-header');
      const headerSection = document.querySelector('.cc-header');
      if (headerSection?.dataset.pbContextWasSticky === 'true') {
        headerSection.classList.add('cc-header--sticky');
        delete headerSection.dataset.pbContextWasSticky;
      }
      document.querySelector('store-header')?.classList.remove('is-out');
    }
  }

  PbContextNavigation.headerOwners = new Set();
  PbContextNavigation.shadowOwners = new Set();
  PbContextNavigation.trackingOwners = new Set();
  customElements.define('pb-context-navigation', PbContextNavigation);
}


